import React, { useState, useEffect } from 'react';
import {
  Wallet,
  Coins,
  Gift,
  Clock,
  Plus,
  Edit2,
  CheckCircle,
  X,
  Trash2,
  Image as ImageIcon,
  Upload,
  Sparkles,
  Eye,
  RotateCcw
} from 'lucide-react';
import { adminApi } from '../../api';
import BrandLogo from '../../components/BrandLogo';
import { formatISTDateTime } from '../../utils/dateUtils';

const PRESET_BRANDS = [
  { name: 'PhonePe Gift Voucher', provider: 'PhonePe', category: 'Recharge & Bills', desc: 'Redeem instantly for mobile recharges, bills & shopping on PhonePe.', logo: 'https://img.icons8.com/color/96/phone-pe.png' },
  { name: 'Flipkart Voucher', provider: 'Flipkart', category: 'Shopping', desc: 'Shop electronics, fashion, and home appliances on Flipkart.', logo: 'https://img.icons8.com/color/96/flipkart.png' },
  { name: 'Amazon Pay Gift Card', provider: 'Amazon Pay', category: 'Shopping', desc: 'Add money directly to your Amazon Pay wallet balance.', logo: 'https://img.icons8.com/color/96/amazon.png' },
  { name: 'Google Play Gift Voucher', provider: 'Google Play', category: 'Games & Entertainment', desc: 'Buy apps, games, books & in-game items on Google Play Store.', logo: 'https://img.icons8.com/color/96/google-play.png' },
  { name: 'Paytm Gift Card', provider: 'Paytm', category: 'Recharge & Payments', desc: 'Add money to Paytm wallet for quick digital payments and bookings.', logo: 'https://img.icons8.com/color/96/paytm.png' },
  { name: 'Myntra Shopping Voucher', provider: 'Myntra', category: 'Fashion & Lifestyle', desc: 'Redeem for trendy apparel, footwear, and accessories on Myntra.', logo: 'https://img.icons8.com/color/96/myntra.png' },
  { name: 'Swiggy Food Voucher', provider: 'Swiggy', category: 'Food & Dining', desc: 'Order meals and groceries with instant delivery on Swiggy.', logo: 'https://img.icons8.com/color/96/swiggy.png' },
  { name: 'Zomato Dining Voucher', provider: 'Zomato', category: 'Food & Dining', desc: 'Order delicious food and restaurant dining experiences across India.', logo: 'https://img.icons8.com/color/96/zomato.png' },
];

export default function AdminWallets() {
  const [activeTab, setActiveTab] = useState('wallets'); // 'wallets', 'withdrawals', 'vouchers'
  const [wallets, setWallets] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [pointsToRupeeRatio, setPointsToRupeeRatio] = useState(10);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [adjustModal, setAdjustModal] = useState({ open: false, user: null, amount: '', type: 'credit', reason: '' });
  const [voucherModal, setVoucherModal] = useState({
    open: false,
    isEdit: false,
    id: null,
    name: '',
    provider: '',
    category: 'Shopping',
    description: '',
    logo: '',
    image_url: '',
    minimum_points: 100,
    inventory_count: 100,
    denominations: '100, 200, 500, 1000, 2000, 5000',
    status: 'active'
  });
  const [statusModal, setStatusModal] = useState({ open: false, withdrawal: null, status: 'Approved', admin_notes: '', voucher_code: '' });
  const [processing, setProcessing] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleImageUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) {
      alert('Image file size should be less than 2.5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setVoucherModal(prev => ({
        ...prev,
        image_url: event.target.result,
        logo: event.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [wRes, wdRes, vRes, sRes] = await Promise.all([
        adminApi.get('/admin/wallets').catch(() => ({ data: { wallets: [] } })),
        adminApi.get('/admin/withdrawals').catch(() => ({ data: { withdrawals: [] } })),
        adminApi.get('/admin/vouchers').catch(() => ({ data: { vouchers: [] } })),
        adminApi.get('/admin/settings').catch(() => ({ data: {} }))
      ]);

      if (wRes.data?.success) {
        setWallets(wRes.data.wallets);
        setSummary(wRes.data.summary);
      }
      if (wdRes.data?.success) {
        setWithdrawals(wdRes.data.withdrawals);
      }
      if (vRes.data?.success) {
        setVouchers(vRes.data.vouchers);
      }
      if (sRes.data?.points_to_rupee_ratio) {
        setPointsToRupeeRatio(sRes.data.points_to_rupee_ratio);
      } else if (sRes.data?.platform_settings?.points_to_rupee_ratio) {
        setPointsToRupeeRatio(sRes.data.platform_settings.points_to_rupee_ratio);
      }
    } catch (err) {
      console.warn('Failed fetching wallet datasets');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 4000);
  };

  // Submit point adjustment
  const handlePointAdjustment = async (e) => {
    e.preventDefault();
    if (!adjustModal.user || !adjustModal.amount) return;
    try {
      setProcessing(true);
      const points = parseInt(adjustModal.amount, 10) * (adjustModal.type === 'debit' ? -1 : 1);
      const res = await adminApi.post('/admin/wallets/adjust', {
        user_id: adjustModal.user.user_id,
        amount: points,
        reason: adjustModal.reason
      });
      if (res.data?.success) {
        showToast(`Points adjusted successfully for ${adjustModal.user.name}`);
        setAdjustModal({ open: false, user: null, amount: '', type: 'credit', reason: '' });
        fetchData();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error adjusting points');
    } finally {
      setProcessing(false);
    }
  };

  // Submit withdrawal status update
  const handleWithdrawalStatusUpdate = async (e) => {
    e.preventDefault();
    if (!statusModal.withdrawal) return;
    try {
      setProcessing(true);
      const res = await adminApi.put(`/admin/withdrawals/${statusModal.withdrawal.id}/status`, {
        status: statusModal.status,
        admin_notes: statusModal.admin_notes,
        voucher_code: statusModal.voucher_code
      });
      if (res.data?.success) {
        showToast(`Withdrawal status updated to ${statusModal.status}`);
        setStatusModal({ open: false, withdrawal: null, status: 'Approved', admin_notes: '', voucher_code: '' });
        fetchData();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating status');
    } finally {
      setProcessing(false);
    }
  };

  // Save Voucher (Create or Update)
  const handleSaveVoucher = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      const chosenLogo = voucherModal.image_url || voucherModal.logo || '🎁';
      const payload = {
        name: voucherModal.name,
        provider: voucherModal.provider,
        category: voucherModal.category || 'Shopping',
        description: voucherModal.description || '',
        logo: chosenLogo,
        image_url: chosenLogo,
        minimum_points: parseInt(voucherModal.minimum_points, 10) || 100,
        inventory_count: parseInt(voucherModal.inventory_count, 10) || 100,
        denominations: voucherModal.denominations ? voucherModal.denominations.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n)) : [100, 200, 500, 1000, 2000, 5000],
        status: voucherModal.status || 'active'
      };

      if (voucherModal.isEdit) {
        await adminApi.put(`/admin/vouchers/${voucherModal.id}`, payload);
        showToast('Voucher updated successfully');
      } else {
        await adminApi.post('/admin/vouchers', payload);
        showToast('New voucher created successfully');
      }
      setVoucherModal({
        open: false,
        isEdit: false,
        id: null,
        name: '',
        provider: '',
        category: 'Shopping',
        description: '',
        logo: '',
        image_url: '',
        minimum_points: 100,
        inventory_count: 100,
        denominations: '100, 200, 500, 1000, 2000, 5000',
        status: 'active'
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving voucher');
    } finally {
      setProcessing(false);
    }
  };

  // Delete Voucher
  const handleDeleteVoucher = async (id, name) => {
    if (!window.confirm(`Are you sure you want to remove ${name} from voucher catalog?`)) return;
    try {
      await adminApi.delete(`/admin/vouchers/${id}`);
      showToast('Voucher removed');
      fetchData();
    } catch (err) {
      alert('Error deleting voucher');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Toast Notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: '#059669',
          color: '#FFFFFF',
          padding: '12px 20px',
          borderRadius: '10px',
          fontWeight: 700,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle size={18} />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Summary KPI Pills */}
      <div
        className="admin-kpi-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px'
        }}
      >
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Wallet size={22} color="#2563EB" />
          </div>
          <div>
            <div style={{ fontSize: '0.76rem', color: '#64748B', fontWeight: 700 }}>Total User Balances</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0F172A' }}>
              {(summary?.total_balance_points || 0).toLocaleString()} pts
            </div>
          </div>
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={22} color="#DC2626" />
          </div>
          <div>
            <div style={{ fontSize: '0.76rem', color: '#64748B', fontWeight: 700 }}>Pending Withdrawals</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#DC2626' }}>
              {withdrawals.filter(w => w.status === 'Pending').length} Requests
            </div>
          </div>
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Gift size={22} color="#7C3AED" />
          </div>
          <div>
            <div style={{ fontSize: '0.76rem', color: '#64748B', fontWeight: 700 }}>Voucher Brands In Stock</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0F172A' }}>
              {vouchers.length} Catalog Items
            </div>
          </div>
        </div>
      </div>

      {/* Main Container with Tabs */}
      <div
        className="admin-card-container"
        style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
      >
        
        {/* Navigation Bar */}
        <div
          className="admin-filter-stack"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}
        >
          <div className="admin-tabs-scroll" style={{ display: 'flex', gap: '8px', background: '#F1F5F9', padding: '4px', borderRadius: '10px' }}>
            <button
              onClick={() => setActiveTab('wallets')}
              style={{
                background: activeTab === 'wallets' ? '#7C3AED' : 'transparent',
                border: 'none',
                color: activeTab === 'wallets' ? '#FFFFFF' : '#475569',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '0.84rem',
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              User Wallets ({wallets.length})
            </button>
            <button
              onClick={() => setActiveTab('withdrawals')}
              style={{
                background: activeTab === 'withdrawals' ? '#7C3AED' : 'transparent',
                border: 'none',
                color: activeTab === 'withdrawals' ? '#FFFFFF' : '#475569',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '0.84rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              <span>Withdrawals</span>
              {withdrawals.filter(w => w.status === 'Pending').length > 0 && (
                <span style={{ background: '#DC2626', color: '#FFFFFF', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '10px', fontWeight: 800 }}>
                  {withdrawals.filter(w => w.status === 'Pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('vouchers')}
              style={{
                background: activeTab === 'vouchers' ? '#7C3AED' : 'transparent',
                border: 'none',
                color: activeTab === 'vouchers' ? '#FFFFFF' : '#475569',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '0.84rem',
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Voucher Inventory ({vouchers.length})
            </button>
          </div>

          {activeTab === 'vouchers' && (
            <button
              onClick={() => setVoucherModal({
                open: true,
                isEdit: false,
                id: null,
                name: '',
                provider: '',
                category: 'Shopping',
                description: '',
                logo: '',
                image_url: '',
                minimum_points: 100,
                inventory_count: 100,
                denominations: '100, 200, 500, 1000, 2000, 5000',
                status: 'active'
              })}
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
                border: 'none',
                color: '#FFFFFF',
                padding: '9px 16px',
                borderRadius: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '0.84rem',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)'
              }}
            >
              <Plus size={16} />
              <span>Add New Voucher</span>
            </button>
          )}
        </div>

        {/* TAB 1: USER WALLETS */}
        {activeTab === 'wallets' && (
          <div className="admin-table-container" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', textAlign: 'left', color: '#64748B' }}>
                  <th style={{ padding: '14px', fontWeight: 700 }}>User Profile</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Available Points</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Rupee Value</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Total Earned</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Total Redeemed</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {wallets.map((w, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ padding: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#FFFFFF' }}>
                          {w.name.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: '#0F172A' }}>{w.name}</div>
                          <div style={{ fontSize: '0.74rem', color: '#64748B' }}>{w.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px', fontSize: '1rem', fontWeight: 800, color: '#059669' }}>
                      {w.available_points.toLocaleString()} pts
                    </td>
                    <td style={{ padding: '14px', fontWeight: 700, color: '#334155' }}>
                      ₹{w.rupee_value.toFixed(2)}
                    </td>
                    <td style={{ padding: '14px', color: '#64748B' }}>
                      {w.total_earned.toLocaleString()} pts
                    </td>
                    <td style={{ padding: '14px', color: '#64748B' }}>
                      {w.total_redeemed.toLocaleString()} pts
                    </td>
                    <td style={{ padding: '14px' }}>
                      <button
                        onClick={() => setAdjustModal({ open: true, user: w, amount: '', type: 'credit', reason: '' })}
                        style={{
                          background: '#F5F3FF',
                          border: '1px solid #DDD6FE',
                          color: '#6D28D9',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Edit2 size={13} />
                        <span>Adjust Balance</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TAB 2: WITHDRAWALS QUEUE */}
        {activeTab === 'withdrawals' && (
          <div>
            <div style={{
              background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)',
              border: '1.5px solid #DDD6FE',
              borderRadius: '14px',
              padding: '14px 18px',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Gift size={22} color="#7C3AED" />
                <div>
                  <div style={{ fontWeight: 800, color: '#4C1D95', fontSize: '0.9rem' }}>Dedicated Gift Card Fulfillment Hub</div>
                  <div style={{ fontSize: '0.78rem', color: '#6D28D9' }}>Fulfill requests with voucher code, PIN, expiry date, instructions & upload voucher image.</div>
                </div>
              </div>
              <a
                href="/admin/gift-cards"
                style={{
                  background: '#7C3AED',
                  color: '#FFFFFF',
                  padding: '8px 16px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)'
                }}
              >
                Open Gift Card Management →
              </a>
            </div>

            <div className="admin-table-container" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', textAlign: 'left', color: '#64748B' }}>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Ref ID</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>User / Email</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Voucher Requested</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Points & Value</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Status</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Submitted (IST)</th>
                  <th style={{ padding: '14px', fontWeight: 700 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#94A3B8' }}>No withdrawal requests found.</td></tr>
                ) : (
                  withdrawals.map((wd, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '14px', fontFamily: 'monospace', color: '#64748B', fontWeight: 600 }}>{wd.reference_id}</td>
                      <td style={{ padding: '14px' }}>
                        <div style={{ fontWeight: 700, color: '#0F172A' }}>{wd.user_name || 'User'}</div>
                        <div style={{ fontSize: '0.74rem', color: '#64748B' }}>{wd.user_details?.email}</div>
                      </td>
                      <td style={{ padding: '14px', color: '#0F172A', fontWeight: 600 }}>{wd.voucher_name}</td>
                      <td style={{ padding: '14px' }}>
                        <div style={{ fontWeight: 800, color: '#D97706' }}>{wd.points} pts</div>
                        <div style={{ fontSize: '0.74rem', color: '#64748B' }}>₹{wd.rupee_value}</div>
                      </td>
                      <td style={{ padding: '14px' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '0.74rem',
                          fontWeight: 700,
                          background: wd.status === 'Fulfilled' ? '#ECFDF5' :
                                      wd.status === 'Approved' ? '#EFF6FF' :
                                      wd.status === 'Rejected' ? '#FEF2F2' : '#FFFBEB',
                          color: wd.status === 'Fulfilled' ? '#059669' :
                                 wd.status === 'Approved' ? '#2563EB' :
                                 wd.status === 'Rejected' ? '#DC2626' : '#D97706'
                        }}>
                          {wd.status}
                        </span>
                      </td>
                      <td style={{ padding: '14px', color: '#64748B', fontSize: '0.78rem' }}>
                        {formatISTDateTime(wd.created_at, true)}
                      </td>
                      <td style={{ padding: '14px' }}>
                        <button
                          onClick={() => setStatusModal({
                            open: true,
                            withdrawal: wd,
                            status: wd.status,
                            admin_notes: wd.admin_notes || '',
                            voucher_code: wd.voucher_code || ''
                          })}
                          style={{
                            background: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            color: '#0F172A',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.78rem',
                            fontWeight: 700
                          }}
                        >
                          Review & Process
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {/* TAB 3: VOUCHERS INVENTORY */}
        {activeTab === 'vouchers' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '18px' }}>
            {vouchers.map((v, idx) => (
              <div
                key={idx}
                style={{
                  background: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '16px',
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <BrandLogo brandName={v.name} image={v.image_url || v.logo} size={48} />
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '10px',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      background: v.status === 'active' ? '#ECFDF5' : '#FEF2F2',
                      color: v.status === 'active' ? '#059669' : '#DC2626'
                    }}>
                      {v.status}
                    </span>
                  </div>

                  <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', fontWeight: 800, color: '#0F172A' }}>{v.name}</h4>
                  <p style={{ margin: '0 0 12px 0', fontSize: '0.78rem', color: '#64748B', lineHeight: 1.4 }}>{v.description}</p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.78rem', color: '#475569', marginBottom: '14px', background: '#F8FAFC', padding: '10px 12px', borderRadius: '10px' }}>
                    <div>Provider / Brand: <strong style={{ color: '#0F172A' }}>{v.provider}</strong></div>
                    <div>Category: <strong style={{ color: '#0F172A' }}>{v.category}</strong></div>
                    <div>Stock Inventory: <strong style={{ color: '#059669' }}>{v.inventory_count} Available</strong> (Used: {v.used_count || 0})</div>
                    <div>Min Points: <strong style={{ color: '#D97706' }}>{v.minimum_points || 100} pts (₹{((v.minimum_points || 100) / (pointsToRupeeRatio || 10)).toFixed(2)})</strong></div>
                    <div>Denominations: <strong style={{ color: '#7C3AED' }}>{Array.isArray(v.denominations) ? v.denominations.join(', ') : '100, 200, 500, 1000'} pts</strong></div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid #E2E8F0', paddingTop: '12px' }}>
                  <button
                    onClick={() => setVoucherModal({
                      open: true,
                      isEdit: true,
                      id: v.id,
                      name: v.name,
                      provider: v.provider,
                      category: v.category || 'Shopping',
                      description: v.description,
                      logo: v.logo || '',
                      image_url: v.image_url || v.logo || '',
                      minimum_points: v.minimum_points || 100,
                      inventory_count: v.inventory_count || 100,
                      denominations: Array.isArray(v.denominations) ? v.denominations.join(', ') : '100, 200, 500, 1000, 2000, 5000',
                      status: v.status || 'active'
                    })}
                    style={{
                      flex: 1,
                      background: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      color: '#0F172A',
                      padding: '8px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <Edit2 size={14} /> Edit
                  </button>

                  <button
                    onClick={() => handleDeleteVoucher(v.id, v.name)}
                    style={{
                      background: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#DC2626',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* MODAL 1: ADJUST POINTS */}
      {adjustModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(3px)' }}>
          <div
            className="admin-modal-box"
            style={{ width: '100%', maxWidth: '440px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0F172A', fontWeight: 800 }}>Adjust User Points</h3>
              <button onClick={() => setAdjustModal({ ...adjustModal, open: false })} style={{ background: 'transparent', border: 'none', color: '#64748B', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <p style={{ fontSize: '0.84rem', color: '#475569', marginBottom: '16px' }}>
              Target User: <strong style={{ color: '#7C3AED' }}>{adjustModal.user?.name}</strong> ({adjustModal.user?.email})
            </p>

            <form onSubmit={handlePointAdjustment} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '6px' }}>Adjustment Type</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setAdjustModal({ ...adjustModal, type: 'credit' })}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: 'none',
                      background: adjustModal.type === 'credit' ? '#059669' : '#F1F5F9',
                      color: adjustModal.type === 'credit' ? '#FFFFFF' : '#475569',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    + Credit Points
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdjustModal({ ...adjustModal, type: 'debit' })}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: 'none',
                      background: adjustModal.type === 'debit' ? '#DC2626' : '#F1F5F9',
                      color: adjustModal.type === 'debit' ? '#FFFFFF' : '#475569',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    - Debit Points
                  </button>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '6px' }}>Points Amount</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={adjustModal.amount}
                  onChange={(e) => setAdjustModal({ ...adjustModal, amount: e.target.value })}
                  placeholder="e.g. 500"
                  style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '6px' }}>Reason / Audit Note</label>
                <input
                  type="text"
                  required
                  value={adjustModal.reason}
                  onChange={(e) => setAdjustModal({ ...adjustModal, reason: e.target.value })}
                  placeholder="e.g. Promotional credit bonus"
                  style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                />
              </div>

              <button
                type="submit"
                disabled={processing}
                style={{
                  marginTop: '8px',
                  background: '#7C3AED',
                  border: 'none',
                  color: '#FFFFFF',
                  padding: '12px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {processing ? 'Processing Adjustment...' : 'Apply Wallet Adjustment'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: REVIEW WITHDRAWAL */}
      {statusModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(3px)' }}>
          <div
            className="admin-modal-box"
            style={{ width: '100%', maxWidth: '480px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0F172A', fontWeight: 800 }}>Process Withdrawal Request</h3>
              <button onClick={() => setStatusModal({ ...statusModal, open: false })} style={{ background: 'transparent', border: 'none', color: '#64748B', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', padding: '14px', borderRadius: '10px', fontSize: '0.84rem', color: '#475569', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div>Reference: <strong style={{ color: '#0F172A' }}>{statusModal.withdrawal?.reference_id}</strong></div>
              <div>Voucher: <strong style={{ color: '#059669' }}>{statusModal.withdrawal?.voucher_name} (₹{statusModal.withdrawal?.rupee_value})</strong></div>
              <div>Points: <strong style={{ color: '#D97706' }}>{statusModal.withdrawal?.points} pts</strong></div>
              <div>User Email: <strong style={{ color: '#0F172A' }}>{statusModal.withdrawal?.user_details?.email}</strong></div>
            </div>

            <form onSubmit={handleWithdrawalStatusUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '6px' }}>Update Status</label>
                <select
                  value={statusModal.status}
                  onChange={(e) => setStatusModal({ ...statusModal, status: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                >
                  <option value="Pending">Pending Review</option>
                  <option value="Approved">Approved (In Batch Payout)</option>
                  <option value="Fulfilled">Fulfilled (Code Delivered)</option>
                  <option value="Rejected">Rejected (Refund Points to User)</option>
                </select>
              </div>

              {statusModal.status === 'Fulfilled' && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '6px' }}>Gift Voucher Claim Code</label>
                  <input
                    type="text"
                    value={statusModal.voucher_code}
                    onChange={(e) => setStatusModal({ ...statusModal, voucher_code: e.target.value })}
                    placeholder="e.g. FLIP-8849-XXXX-2026"
                    style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                  />
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '6px' }}>Admin Notes</label>
                <textarea
                  rows="3"
                  value={statusModal.admin_notes}
                  onChange={(e) => setStatusModal({ ...statusModal, admin_notes: e.target.value })}
                  placeholder="e.g. Voucher code delivered to user email."
                  style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none', resize: 'none' }}
                />
              </div>

              <button
                type="submit"
                disabled={processing}
                style={{
                  marginTop: '8px',
                  background: '#7C3AED',
                  border: 'none',
                  color: '#FFFFFF',
                  padding: '12px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {processing ? 'Updating...' : 'Save & Confirm Status'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: ADD/EDIT VOUCHER WITH IMAGE UPLOAD & PRESET BRANDS */}
      {voucherModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backdropFilter: 'blur(4px)' }}>
          <div
            className="admin-modal-box"
            style={{ width: '100%', maxWidth: '580px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '20px', padding: '24px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #F1F5F9', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Gift size={22} color="#7C3AED" />
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0F172A', fontWeight: 800 }}>
                  {voucherModal.isEdit ? 'Edit Gift Voucher' : 'Add New Gift Voucher'}
                </h3>
              </div>
              <button onClick={() => setVoucherModal({ ...voucherModal, open: false })} style={{ background: '#F1F5F9', border: 'none', color: '#64748B', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} /></button>
            </div>

            {/* PRESET BRAND QUICK SELECTOR */}
            {!voucherModal.isEdit && (
              <div style={{ marginBottom: '16px', background: '#F8FAFC', padding: '12px 14px', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 800, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Sparkles size={14} color="#7C3AED" /> Quick Fill with Popular Brand Preset:
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {PRESET_BRANDS.map((p, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setVoucherModal(prev => ({
                          ...prev,
                          name: p.name,
                          provider: p.provider,
                          category: p.category,
                          description: p.desc,
                          logo: p.logo,
                          image_url: p.logo,
                          minimum_points: 100,
                          denominations: '100, 200, 500, 1000, 2000, 5000'
                        }));
                      }}
                      style={{
                        padding: '5px 10px',
                        borderRadius: '8px',
                        border: voucherModal.provider.toLowerCase() === p.provider.toLowerCase() ? '1.5px solid #7C3AED' : '1px solid #CBD5E1',
                        background: voucherModal.provider.toLowerCase() === p.provider.toLowerCase() ? '#EDE9FE' : '#FFFFFF',
                        color: voucherModal.provider.toLowerCase() === p.provider.toLowerCase() ? '#6D28D9' : '#334155',
                        fontSize: '0.74rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      <BrandLogo brandName={p.name} image={p.logo} size={18} />
                      <span>{p.provider}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* LIVE CUSTOMER APP PREVIEW CARD */}
            <div style={{
              background: 'linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)',
              border: '1.5px solid #DDD6FE',
              borderRadius: '14px',
              padding: '12px 14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <BrandLogo brandName={voucherModal.name} image={voucherModal.image_url || voucherModal.logo} size={44} />
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#6D28D9', fontWeight: 800, textTransform: 'uppercase' }}>Customer Wallet Preview</div>
                  <div style={{ fontWeight: 800, color: '#1E1B4B', fontSize: '0.92rem' }}>
                    {voucherModal.name || 'Voucher Name'}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600 }}>
                    Min: ₹{((parseInt(voucherModal.minimum_points, 10) || 100) / (pointsToRupeeRatio || 10)).toFixed(2)} ({voucherModal.minimum_points || 100} Pts)
                  </div>
                </div>
              </div>
              <span style={{
                background: '#FFFFFF',
                border: '1px solid #C4B5FD',
                color: '#6D28D9',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.72rem',
                fontWeight: 800
              }}>
                {voucherModal.category || 'Shopping'}
              </span>
            </div>

            <form onSubmit={handleSaveVoucher} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* IMAGE UPLOAD & LOGO URL SECTION */}
              <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px 14px' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#334155', fontWeight: 800, marginBottom: '8px' }}>
                  Brand Image / Logo (Appears on Customer Wallet Page)
                </label>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <BrandLogo brandName={voucherModal.name} image={voucherModal.image_url || voucherModal.logo} size={48} />
                  
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '200px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <label style={{
                        background: '#7C3AED',
                        color: '#FFFFFF',
                        padding: '7px 14px',
                        borderRadius: '8px',
                        fontSize: '0.76rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 6px rgba(124, 58, 237, 0.25)'
                      }}>
                        <Upload size={14} /> Upload Image File
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                        />
                      </label>

                      {(voucherModal.image_url || voucherModal.logo) && (
                        <button
                          type="button"
                          onClick={() => setVoucherModal(prev => ({ ...prev, image_url: '', logo: '' }))}
                          style={{
                            background: '#FEE2E2',
                            border: '1px solid #FECACA',
                            color: '#DC2626',
                            padding: '6px 10px',
                            borderRadius: '8px',
                            fontSize: '0.74rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          Clear Image
                        </button>
                      )}
                    </div>

                    <input
                      type="text"
                      value={voucherModal.image_url || ''}
                      onChange={(e) => setVoucherModal({ ...voucherModal, image_url: e.target.value, logo: e.target.value })}
                      placeholder="Or enter Image URL (e.g. https://...)"
                      style={{ width: '100%', boxSizing: 'border-box', background: '#FFFFFF', border: '1px solid #CBD5E1', padding: '7px 10px', borderRadius: '6px', fontSize: '0.76rem', color: '#0F172A', outline: 'none' }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '4px' }}>Voucher Name</label>
                <input
                  type="text"
                  required
                  value={voucherModal.name}
                  onChange={(e) => setVoucherModal({ ...voucherModal, name: e.target.value })}
                  placeholder="e.g. Myntra Shopping Voucher"
                  style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '4px' }}>Provider / Brand Name</label>
                  <input
                    type="text"
                    required
                    value={voucherModal.provider}
                    onChange={(e) => setVoucherModal({ ...voucherModal, provider: e.target.value })}
                    placeholder="e.g. Myntra"
                    style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '4px' }}>Category</label>
                  <input
                    type="text"
                    value={voucherModal.category}
                    onChange={(e) => setVoucherModal({ ...voucherModal, category: e.target.value })}
                    placeholder="e.g. Shopping, Recharge, Food"
                    style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '4px' }}>Description</label>
                <textarea
                  rows="2"
                  value={voucherModal.description}
                  onChange={(e) => setVoucherModal({ ...voucherModal, description: e.target.value })}
                  placeholder="Redeem for trendy apparel on Myntra."
                  style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none', resize: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '4px' }}>Stock Inventory Count</label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={voucherModal.inventory_count}
                    onChange={(e) => setVoucherModal({ ...voucherModal, inventory_count: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '4px' }}>Minimum Points (₹10 = 100 Pts)</label>
                  <input
                    type="number"
                    min="100"
                    required
                    value={voucherModal.minimum_points}
                    onChange={(e) => setVoucherModal({ ...voucherModal, minimum_points: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '4px' }}>Denominations (pts, comma-separated)</label>
                  <input
                    type="text"
                    value={voucherModal.denominations}
                    onChange={(e) => setVoucherModal({ ...voucherModal, denominations: e.target.value })}
                    placeholder="100, 200, 500, 1000, 2000, 5000"
                    style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 14px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                  />
                </div>
                <div style={{ width: '120px' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', fontWeight: 700, marginBottom: '4px' }}>Status</label>
                  <select
                    value={voucherModal.status}
                    onChange={(e) => setVoucherModal({ ...voucherModal, status: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', background: '#F8FAFC', border: '1px solid #CBD5E1', padding: '10px 8px', borderRadius: '8px', color: '#0F172A', outline: 'none' }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={processing}
                style={{
                  marginTop: '10px',
                  background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
                  border: 'none',
                  color: '#FFFFFF',
                  padding: '13px',
                  borderRadius: '12px',
                  fontWeight: 800,
                  fontSize: '0.92rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(124, 58, 237, 0.35)'
                }}
              >
                {processing ? 'Saving...' : (voucherModal.isEdit ? 'Update Voucher' : 'Create Voucher')}
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
